<script setup lang="ts">
import { Link, useForm } from "@inertiajs/vue3"
import { computed } from "vue"
import { UiButton, UiFormLayout, UiSelect, UiTextInput } from "@/components"
import { useNotifications } from "@/composables/useNotifications"

type UserPayload = {
  id?: number | null
  name: string
  email: string
  role_key: string
  company_id: number | ""
}

const props = defineProps<{
  mode: "create" | "edit"
  title: string
  subtitle?: string
  user: UserPayload
  companies: Array<{ id: number; name: string }>
  roleOptions: Array<{ label: string; value: string }>
  errors?: Record<string, string[]>
}>()

const form = useForm({
  user: {
    name: props.user.name,
    email: props.user.email,
    role_key: props.user.role_key,
    company_id: props.user.company_id,
    password: "",
    password_confirmation: "",
  },
})

const notify = useNotifications()
const companyOptions = computed(() => props.companies.map((company) => ({ label: company.name, value: company.id })))
const fieldError = (field: string) => props.errors?.[field]?.[0]

function submit() {
  const options = {
    onSuccess: () => notify.success(props.mode === "create" ? "Usuario criado com sucesso" : "Usuario atualizado com sucesso"),
  }

  if (props.mode === "create") {
    form.post("/users/create_record", options)
    return
  }

  form.put(`/users/${props.user.id}`, options)
}
</script>

<template>
  <UiFormLayout :title="title" :subtitle="subtitle" back-href="/users" back-label="Voltar para usuarios">
    <form class="space-y-4" @submit.prevent="submit">
      <div class="grid gap-4 md:grid-cols-2">
        <UiTextInput id="user-name" v-model="form.user.name" label="Nome" :error="fieldError('name')" />
        <UiTextInput id="user-email" v-model="form.user.email" type="email" label="Email" :error="fieldError('email')" />
      </div>

      <div class="grid gap-4 md:grid-cols-2">
        <UiSelect
          id="user-company"
          :model-value="form.user.company_id"
          label="Empresa"
          :options="companyOptions"
          :error="fieldError('company_id')"
          @update:model-value="form.user.company_id = $event as number | ''"
        />

        <UiSelect
          id="user-role"
          :model-value="form.user.role_key"
          label="Perfil"
          :options="roleOptions"
          :error="fieldError('role_key')"
          @update:model-value="form.user.role_key = String($event || '')"
        />
      </div>

      <section class="rounded-md border border-slate-200 p-4">
        <h2 class="mb-2 text-sm font-semibold text-slate-800">Senha</h2>
        <p class="mb-3 text-xs text-slate-500">
          {{ mode === "create" ? "Defina uma senha inicial para o usuario." : "Preencha apenas se desejar trocar a senha." }}
        </p>

        <div class="grid gap-4 md:grid-cols-2">
          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700" for="user-password">Senha</label>
            <input
              id="user-password"
              v-model="form.user.password"
              type="password"
              class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 transition focus:border-slate-400 focus:outline-none"
            />
            <p v-if="fieldError('password')" class="mt-1 text-sm text-rose-600">{{ fieldError("password") }}</p>
          </div>

          <div>
            <label class="mb-1 block text-sm font-medium text-slate-700" for="user-password-confirmation">Confirmar senha</label>
            <input
              id="user-password-confirmation"
              v-model="form.user.password_confirmation"
              type="password"
              class="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 transition focus:border-slate-400 focus:outline-none"
            />
            <p v-if="fieldError('password_confirmation')" class="mt-1 text-sm text-rose-600">{{ fieldError("password_confirmation") }}</p>
          </div>
        </div>
      </section>

      <p v-if="fieldError('base')" class="text-sm text-rose-600">{{ fieldError('base') }}</p>

      <div class="flex items-center gap-3 pt-2">
        <UiButton type="submit" :disabled="form.processing">
          {{ mode === "create" ? "Criar usuario" : "Salvar alteracoes" }}
        </UiButton>

        <Link href="/users" class="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100">
          Cancelar
        </Link>
      </div>
    </form>
  </UiFormLayout>
</template>

